import React, { useEffect, useState } from "react";
import "./SocMonitoringLab.css";

function SocMonitoringLab() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ severity: "ALL", source: "ALL" });
  const [stats, setStats] = useState({ normal: 0, suspicious: 0, malicious: 0 });
  const [loading, setLoading] = useState(false);

  const normalizeLog = (line) => ({
    timestamp: new Date().toISOString(),
    source: "SOC",
    severity: line.includes("CRITICAL") ? "CRITICAL" : "INFO",
    message: line,
  });

  const initSim = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ method: "soc", action: "init" }),
        }
      );
      const resp = await res.json();
      const data = JSON.parse(resp.body);

      const logs = (data.terminal || "")
        .split("\n")
        .filter(Boolean)
        .map(normalizeLog);

      setLogs(logs);
      setStats({ normal: data.count || 0, suspicious: 0, malicious: 0 });
    } catch (e) {
      console.error("Init error:", e);
    }
    setLoading(false);
  };

  const genLog = async () => {
    try {
      const res = await fetch(
        "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ method: "soc", action: "tick" }),
        }
      );
      const resp = await res.json();
      const data = JSON.parse(resp.body);

      const newLogs = (data.terminal || "")
        .split("\n")
        .filter(Boolean)
        .map(normalizeLog);

      setLogs((prev) => [...newLogs, ...prev]);
      setStats({ normal: data.count || 0, suspicious: 0, malicious: 0 });
    } catch (e) {
      console.error("Tick error:", e);
    }
  };

  useEffect(() => {
    initSim();
  }, []);

  const filteredLogs = logs.filter((log) => {
    const sev = log.severity.toUpperCase();
    const src = log.source.toUpperCase();
    const fSev = filters.severity.toUpperCase();
    const fSrc = filters.source.toUpperCase();
    if (fSev !== "ALL" && sev !== fSev) return false;
    if (fSrc !== "ALL" && src !== fSrc) return false;
    return true;
  });

  return (
    <div className="soc-container">
      <h1 className="soc-title">📊 SOC Monitoring Lab</h1>
      <p className="soc-sub">
        Simulate security events, filter logs & monitor threats in real time.
      </p>

      <div className="soc-layout">
        {/* Left column */}
        <div className="soc-left">
          {/* Controls */}
          <div className="controls">
            <h3>Controls</h3>
            <div className="row">
              <label>Severity Filter:</label>
              <select
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              >
                <option>ALL</option>
                <option>INFO</option>
                <option>WARNING</option>
                <option>CRITICAL</option>
              </select>
            </div>
            <div className="row">
              <label>Source Filter:</label>
              <select
                value={filters.source}
                onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              >
                <option>ALL</option>
                <option>SOC</option> {/* ✅ Added SOC so logs appear */}
                <option>WEB</option>
                <option>NETWORK</option>
                <option>ENDPOINT</option>
              </select>
            </div>
            <div className="buttons">
              <button onClick={genLog} disabled={loading}>
                ➕ Generate Log
              </button>
              <button className="ghost" onClick={initSim} disabled={loading}>
                🔄 Reset
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="stats">
            <div className="stat allowed">✅ Normal: {stats.normal}</div>
            <div className="stat suspicious">⚠ Suspicious: {stats.suspicious}</div>
            <div className="stat blocked">⛔ Malicious: {stats.malicious}</div>
          </div>
        </div>

        {/* Right column */}
        <div className="soc-right">
          <div className="log-box">
            <div className="log-header">
              <span>Timestamp</span>
              <span>Source</span>
              <span>Severity</span>
              <span>Message</span>
            </div>
            <div className="log-list">
              {filteredLogs.length === 0 ? (
                <div className="log-empty">No logs available.</div>
              ) : (
                filteredLogs.map((log, i) => (
                  <div
                    key={i}
                    className={`log-row ${
                      log.severity === "CRITICAL"
                        ? "critical"
                        : log.severity === "WARNING"
                        ? "warning"
                        : "info"
                    }`}
                  >
                    <span>{log.timestamp}</span>
                    <span>{log.source}</span>
                    <span>{log.severity}</span>
                    <span className="msg">{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Terminal-like view */}
      <div className="soc-terminal">
        <h3>🔍 Analyst Terminal</h3>
        <div className="terminal">
          {logs.slice(0, 8).map((log, i) => (
            <div key={i}>
              [{log.timestamp}] ({log.source}) {log.severity}: {log.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SocMonitoringLab;
